<template>
  <div class="text-center">
    <v-dialog v-model="dialog" width="500">
      <template v-slot:activator="{ on, attrs }">
        <v-btn block dense color="red lighten-2" dark v-bind="attrs" v-on="on">
          <v-icon>mdi-file-chart</v-icon> Reports
        </v-btn>
      </template>

      <v-card>
        <v-card-title class="text-h5 pa-2 primary white--text">
          Purchase Reports
        </v-card-title>

        <v-container>
          <div class="flex space-x-2.5">
            <v-text-field
              v-model="options.dateStart"
              type="date"
              hide-details="auto"
              outlined
              dense
              label="Date Start"
            />
            <v-text-field
              v-model="options.dateEnd"
              type="date"
              hide-details="auto"
              outlined
              dense
              label="Date End"
            />
          </div>
        </v-container>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <div>
            <div class="flex space-x-2.5">
              <v-btn
                :loading="fetching"
                color="error"
                dense
                @click="getReportPDF"
              >
                PDF
              </v-btn>
              <download-excel
                :fetch="getReportRows"
                type="xls"
                worksheet="purchases"
                :title="`Purchase Report ${storeName}`"
                :name="excelFileName"
                :fields="excelFields"
              >
                <v-btn :loading="fetching" color="primary" dense>
                  Excel
                </v-btn>
              </download-excel>
            </div>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import rcvInvRepository from '@/repositories/recieved-inventory.repository'
import { formatAccounting, getCurrentDate } from '../../../../lib/utils'
import PurchaseReport from '../../../../pdf/purchase-reports.pdf'

export default {
  name: 'ReportRecievedInventories',
  props: {
    dateStart: {
      type: String,
      default: null,
    },
    dateEnd: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      fetching: false,
      options: {
        dateStart: null,
        dateEnd: null,
      },
      dialog: false,
    }
  },
  computed: {
    ...mapState({
      storeId: (state) => state.settings.storeId,
      stores: (state) => state.branchLocation.list,
    }),
    storeName() {
      return this.stores.find((e) => e.id === this.storeId)?.name ?? ''
    },
    excelFileName() {
      return `${this.storeName}_purchase_report_${new Date().getTime()}.xls`
    },
    excelFields() {
      const money = (value) => `${Number(value || 0).toFixed(2)}`
      return {
        'Receipt No': 'receipt_no',
        'Product Name': 'product_name',
        Unit: 'unit',
        Qty: 'quantity',
        Cost: { field: 'cost', callback: money },
        Total: { field: 'total', callback: money },
        Amount: { field: 'amount', callback: money },
        'Date Of Purchase': {
          field: 'date_purchased',
          callback: (value) => getCurrentDate(new Date(value), 'en-US'),
        },
      }
    },
  },
  watch: {
    dialog(open) {
      if (open) {
        this.options = {
          dateStart: this.dateStart || this.options.dateStart,
          dateEnd: this.dateEnd || this.options.dateEnd,
        }
      }
    },
  },
  methods: {
    async getReport() {
      const params = {
        ...this.options,
        storeId: this.storeId,
      }
      const { data } = await rcvInvRepository.report(params)
      return data
    },
    async getReportRows() {
      if (this.fetching) return []
      try {
        this.fetching = true
        const data = await this.getReport()
        return data.items
      } catch (e) {
        console.error(e)
        this.$alertSwalError('oops something went wrong')
        return []
      } finally {
        this.fetching = false
      }
    },
    async getReportPDF() {
      if (this.fetching) return
      try {
        this.fetching = true
        const data = await this.getReport()
        const report = new PurchaseReport()
        // Column alignment lives in the PDF's columnStyles so the two exports
        // stay in step; rows only carry values
        const items = data.items.map((item) => {
          return [
            item.receipt_no,
            item.product_name || item.stock_code || '',
            item.unit || '',
            `${Number(item.quantity || 0)}`,
            formatAccounting(Number(item.cost)),
            formatAccounting(Number(item.total)),
            formatAccounting(Number(item.amount)),
            getCurrentDate(new Date(item.date_purchased), 'en-US'),
          ]
        })
        const storeName = data.store.toLowerCase()
        report.generate(
          {
            namePrefix: storeName,
            storeName: data.store,
            dateRange: `${getCurrentDate(
              new Date(data.date_start),
              'en-US',
            )} - ${getCurrentDate(new Date(data.date_end), 'en-US')}`,
            totalAmount: formatAccounting(Number(data.total_amount)),
          },
          items,
        )
        this.dialog = false
      } catch (e) {
        console.error(e)
        this.$alertSwalError('oops something went wrong')
      } finally {
        this.fetching = false
      }
    },
  },
}
</script>
